import { Fragment, FunctionComponent, useState } from 'react';
import { Form, Formik } from 'formik';
import { boolean, object, string } from 'yup';
import Link from 'next/link';

import { Button, Checkbox, Modal, Text, Textarea, Textinput } from '@/atoms';

import { routes } from '@/lib/routes';

import { TypeContactFormProps } from './types';
import { ButtonVariant } from '@/atoms/button/types';
import Script from 'next/script';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Select } from '@/atoms/select/Select';
import { Option } from '@/atoms/select/childs/Option';
import { FileUpload } from '@/atoms/file-upload/file-upload';
import { PopUpContact } from '@/organisms/popup-contact';
import MoonLoader from 'react-spinners/MoonLoader';

export const ContactForm: FunctionComponent<TypeContactFormProps> = ({
    title,
    subtitle,
    backgroundColor,
    textButton,
    nameLabel,
    surnameLabel,
    emailLabel,
    messageLabel,
    labelCkeckDown,
    className = '',
    children,
}: TypeContactFormProps) => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [isPopUpVisible, setIsPopUpVisible] = useState(false);
    const INPUT_TOO_SHORT_MESSAGE = 'Demasiado corto!';
    const INPUT_TOO_LONG_MESSAGE = 'Demasiado largo';

    const handleTextClick = () => {
        setIsPopUpVisible(true);
    };

    const handleClosePopUp = () => {
        setIsPopUpVisible(false);
    };

    const RegisterSchema = object().shape({
        name: string()
            .required('El nombre es obligatorio')
            .min(2, INPUT_TOO_SHORT_MESSAGE)
            .max(50, INPUT_TOO_LONG_MESSAGE),
        surname: string()
            .required('El campo apellidos es obligatorio')
            .min(2, INPUT_TOO_SHORT_MESSAGE)
            .max(50, INPUT_TOO_LONG_MESSAGE),
        email: string()
            .required('El email es obligatorio')
            .min(2, INPUT_TOO_SHORT_MESSAGE)
            .max(50, INPUT_TOO_LONG_MESSAGE),
        telephone: string().when('contact_reason', {
            is: (val) => val === 'claims',
            then: string().required('El teléfono es obligatorio'),
        }),
        contact_reason: string()
            .required("El campo información de contacto es obligatorio"),
        product_name: string().when('contact_reason', {
            is: (val) => val === 'claims' || val === "partnerships",
            then: string().required('El nombre de producto es obligatorio'),
        }),
        batch_number: string().when('contact_reason', {
            is: (val) => val === 'claims',
            then: string().required('El número de lote es obligatorio'),
        }),
        message: string().when('contact_reason', {
            is: (val) => val === "claims" || val === "information" || val === "partnerships" || val === "customer_service" || val === "incidences" || val === "other",
            then: string().required('El mensaje es obligatorio')
                .min(2, INPUT_TOO_SHORT_MESSAGE)
                .max(500, INPUT_TOO_LONG_MESSAGE),
        }),
        accept_privacy: boolean()
            .oneOf([true], 'Debes aceptar las políticas de privacidad'),
        accept_commercial: boolean(),
    });
    const initialValues = {
        name: '',
        surname: '',
        message: '',
        email: '',
        telephone: '',
        address: '',
        postal_code: '',
        city: '',
        country: '',
        files: '',
        contact_reason: '',
        brand: 'Navidul',
        batch_number: '',
        barcode: '',
        product_name: '',
        expiration_date: '',
        order_number: '',
        accept_privacy: false,
        accept_commercial: false,
    };

    const [isOpen, setModalOpen] = useState(false);
    const [isOpenError, setModalOpenError] = useState(false);
    const [filesId, setFilesId] = useState([]);

    return (
        <div style={{ backgroundColor: backgroundColor }} className={className} >
            <div className="flex flex-col">
                <Text className="text-subtitle text-subtitle--l mb-4">{title}</Text>
                {subtitle && (
                    <Text className="text-body text-body--m mb-6">{subtitle}</Text>
                )}
                <Formik
                    onSubmit={async ({
                        name,
                        surname,
                        message,
                        email,
                        telephone,
                        address,
                        postal_code,
                        city,
                        country,
                        contact_reason,
                        accept_privacy,
                        accept_commercial,
                        brand,
                        barcode,
                        batch_number,
                        product_name,
                        expiration_date,
                        order_number,
                    }, { resetForm }) => {
                        const json = {
                            webform_id: 'contact',
                            name: name,
                            surname: surname,
                            email: email,
                            telephone: telephone,
                            address: address,
                            postal_code: postal_code,
                            city: city,
                            country: country,
                            message: message,
                            contact_reason: contact_reason,
                            barcode: barcode,
                            brand: brand,
                            batch_number: batch_number,
                            product_name: product_name,
                            expiration_date: expiration_date,
                            order_number: order_number,
                            files: filesId.length > 0 ? filesId : null,
                            accept_privacy: accept_privacy,
                            accept_commercial: accept_commercial,
                            gReCaptchaToken: await executeRecaptcha("enquiryFormSubmit"),
                        };

                        const response = await fetch('/api/submitform', {
                            method: "POST",
                            body: JSON.stringify(json),
                        });
                        response.ok && (
                            <Script id="form-send-ok" strategy="afterInteractive">
                                {`
                                    window.dataLayer.push({
                                        'event': 'generate_lead',
                                        'descripcion': ${message},
                                        'user_id': ''
                                    });
                                `}
                            </Script>
                        );
                        response.ok && setModalOpen(true);
                        !response.ok && setModalOpenError(true);
                        resetForm({ values: initialValues })
                    }}
                    validationSchema={RegisterSchema}
                    initialValues={initialValues}
                >
                    {({
                        setFieldTouched, values, isSubmitting
                    }) => (
                        <Form>
                            <div className="flex flex-col lg:gap-4">
                                <div className="lg:flex gap-4">
                                    <div className="mb-4 lg:w-1/2">
                                        <Textinput label={nameLabel} name="name" />
                                    </div>
                                    <div className="mb-4 lg:w-1/2">
                                        <Textinput
                                            label={surnameLabel}
                                            name="surname"
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                                <div className="lg:flex gap-4">
                                    <div className="mb-4 lg:w-1/2">
                                        <Textinput
                                            label={emailLabel}
                                            name="email"
                                            type="email"
                                            className="w-full"
                                            placeholder="ejemplo@gmail.com"
                                        />
                                    </div>
                                    <div className="mb-4 lg:w-1/2">
                                        <Textinput
                                            label="Teléfono"
                                            name="telephone"
                                            type="number"
                                            className="w-full"
                                        />
                                    </div>
                                </div>

                                <div className="mb-4 w-full">
                                    <Textinput
                                        label="Dirección"
                                        name="address"
                                    />
                                </div>
                                <div className="lg:flex gap-4">
                                    <div className="mb-4 lg:w-1/3">
                                        <Textinput
                                            label="Código postal"
                                            name="postal_code"
                                        />
                                    </div>
                                    <div className="mb-4 lg:w-1/3">
                                        <Textinput
                                            label="Ciudad"
                                            name="city"
                                            className="w-full"
                                        />
                                    </div>
                                    <div className="mb-4 lg:w-1/3">
                                        <Textinput
                                            label="País"
                                            name="country"
                                            className="w-full"
                                        />
                                    </div>
                                </div>

                                <div className="lg:flex gap-4">
                                    <div className="mb-4 lg:w-1/2">
                                        <Select padding="p-2" placeholder="Selecciona una opción" label="Selecciona tu motivo de contacto*" name="contact_reason" >
                                            <Option value="information">Información sobre nuestros productos (alérgenos, nutricional,…)</Option>
                                            <Option value="claims">Quejas sobre la calidad de nuestros productos</Option>
                                            <Option value="partnerships">Preguntas sobre promociones</Option>
                                            <Option value="customer_service">Servicio al cliente (facturas, pedidos...)</Option>
                                            <Option value="incidences">Consultas/incidencias venta on-line</Option>
                                            <Option value="other">Otros</Option>
                                        </Select>
                                    </div>
                                    <div className={`lg:w-1/2 ${values.contact_reason != "" ? "mb-4" : "mb-6"}`}>
                                        <Textinput
                                            disabled
                                            label="Marca"
                                            value="Navidul"
                                            placeholder="Navidul"
                                            name="brand"
                                            labelOpacity={false}
                                            className="w-full opacity-100"
                                        />
                                    </div>
                                </div>

                                {values.contact_reason != "" ?
                                    <>

                                        {values.contact_reason == "claims" ?
                                            <>
                                                <div className="lg:flex gap-4">
                                                    <div className="mb-4 lg:w-1/2">
                                                        <Textinput
                                                            label="Nombre de producto*"
                                                            name="product_name"
                                                        />
                                                    </div>
                                                    <div className="mb-4 lg:w-1/2">
                                                        <Textinput
                                                            label="Número de lote*"
                                                            name="batch_number"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="lg:flex gap-4">
                                                    <div className="lg:w-1/2 mb-4">
                                                        <Textinput
                                                            label="Código de barras"
                                                            name="barcode"
                                                        />
                                                    </div>
                                                    <div className="w-full lg:w-1/2 mb-4">
                                                        <Textinput
                                                            label="Fecha consumo preferente"
                                                            name="expiration_date"
                                                            type="date"
                                                            placeholder="dd/mm/aaaa"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="w-full mb-4">
                                                    <p onClick={handleTextClick} className="cursor-pointer text-primary-500 hover:text-primary-600 !leading-5 text-body text-body-m">¿Cómo puedo obtener los datos de los campos número de lote, código de barras y fecha de consumo preferente ?</p>
                                                </div>
                                                {isPopUpVisible && <PopUpContact onClose={() => handleClosePopUp} />}
                                            </> : null}

                                        {values.contact_reason == "partnerships" ?
                                            <>
                                                <div className="mb-4 w-full">
                                                    <Textinput
                                                        label="Nombre de producto*"
                                                        name="product_name"
                                                        className="w-full"
                                                    />
                                                </div>
                                            </> : null}

                                        {values.contact_reason == "incidences" ?
                                            <>
                                                <div className="mb-4 w-full">
                                                    <Textinput
                                                        label="Número de pedido"
                                                        name="order_number"
                                                        className="w-full"
                                                    />
                                                </div>
                                            </> : null}


                                        <div className="mb-6 w-full">
                                            <Textarea
                                                label={messageLabel}
                                                name="message"
                                                placeholder="Mensaje"
                                                rows={5}
                                                cols={50}
                                            />
                                        </div>

                                        <div className="mb-4 w-full">
                                            <FileUpload setFilesId={setFilesId} filesId={filesId} setFieldTouched={() => setFieldTouched} />
                                        </div>
                                    </>
                                    : null}
                            </div>
                            <div className="mb-3">
                                <Checkbox name="accept_privacy" checked={values.accept_privacy}>
                                    {children}{' '}
                                </Checkbox>
                            </div>
                            <div className="mb-6">
                                <Checkbox name="accept_commercial" checked={values.accept_commercial}>{labelCkeckDown}</Checkbox>
                            </div>
                            <p className="text-body text-xs md:text-body--s my-4 text-justify">
                                Campofrío Food Group, S.A.U (CFG) utilizará los datos que proporcione en este formulario con la finalidad de atender la consulta planteada por este medio. La base de legitimación para el tratamiento de sus datos por parte de CFG se encuentra en la relación contractual entablada entre el usuario y CFG. Puede ejercitar sus derechos de protección de datos dirigiéndose por escrito al domicilio social en Avenida de Europa 24, 28108, Alcobendas, Madrid o a través del correo electrónico <Link className="text-primary-500 hover:text-primary-600 break-words" href="mailto:dpo.external@campofriofg.com">dpo.external@campofriofg.com</Link>. Más información: consultando nuestra <Link href="/es/politica-privacidad" className="text-primary-500 hover:text-primary-600 break-words">Política de Privacidad</Link>.
                            </p>
                            <p className="text-body text-xs md:text-body--s my-4">
                                Este sitio está protegido por reCAPTCHA.
                            </p>

                            {isSubmitting ?
                                <div className="flex justify-center">
                                    <MoonLoader color="#c78938" loading={true} speedMultiplier={0.6} className="spinner" />
                                </div>
                                :
                                <Button
                                    variant={ButtonVariant.PRIMARY}
                                    type="submit"
                                    disabled={!values.accept_privacy || isSubmitting}
                                >
                                    {textButton}
                                </Button>
                            }

                            {/* Modal de enviado formulario */}
                            <Modal
                                isOpen={isOpen}
                                closeModal={() => setModalOpen(false)}
                                className="sm:w-[590px]"
                            >
                                <Fragment key="0">
                                    <div className="flex flex-col gap-10">
                                        <div className="flex flex-col gap-4 text-center">
                                            <Text className="text-subtitle text-subtitle--l">
                                                ¡Gracias por escribirnos!
                                            </Text>
                                            <Text className="text-body text-body--l">
                                                El formulario se envio con exito. Nos pondremos en
                                                contacto contigo lo antes posible
                                            </Text>
                                        </div>
                                        <div className="flex flex-col justify-center gap-5 md:flex-row">
                                            <Link
                                                href={routes.home}
                                                passHref
                                                prefetch={false}
                                                legacyBehavior
                                            >
                                                <Button as="a" variant={ButtonVariant.PRIMARY}>
                                                    volver a la tienda
                                                </Button>
                                            </Link>
                                        </div>
                                    </div>
                                </Fragment>
                            </Modal>

                            {/* Modal del Error del formulario */}
                            <Modal
                                isOpen={isOpenError}
                                closeModal={() => setModalOpenError(false)}
                                className="sm:w-[590px]"
                            >
                                <Fragment key="0">
                                    <div className="flex flex-col gap-10">
                                        <div className="flex flex-col gap-4 text-center">
                                            <Text className="text-subtitle text-subtitle--l">
                                                ¡Vaya!
                                            </Text>
                                            <Text className="text-body text-body--l">
                                                El formulario no se ha podido enviar por algún motivo.
                                                Actualiza la página y vuelvelo a intentar. Como
                                                alternativa puedes llamarnos por teléfono de Lunes a
                                                <span className="text-primary-500">
                                                    {' '}
                                                    Viernes de 9:00 - 14:00
                                                </span>
                                            </Text>
                                        </div>
                                        <div className="flex flex-col justify-center gap-5 md:flex-row">
                                            <Button
                                                variant={ButtonVariant.PRIMARY}
                                                onClick={() => setModalOpenError(false)}
                                            >
                                                volver a intentarlo
                                            </Button>
                                        </div>
                                    </div>
                                </Fragment>
                            </Modal>
                        </Form>
                    )}
                </Formik>
            </div>
        </div >
    );
};

ContactForm.displayName = 'ContactForm';
